/**
 * Platform tenants — Wave 1a / W1a-17 + W1a-18+ (extends as Stage 4 progresses).
 *
 * TanStack Query hooks for `/api/v1/admin/platform/tenants`. Sysadmin token
 * (audience `argusai-admin`) flows automatically via the apiClient.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';
import type { McpTenantConfigInput } from '@argusai/shared/schemas/mcp-install.schema';

// ─── Wire types ──────────────────────────────────────────────────────────────

export type TenantStatus = 'active' | 'suspended' | 'deleted' | 'pending';
export type TenantMode = 'standalone' | 'iq_connected';

export interface TenantListItem {
  id: string;
  name: string;
  slug: string;
  orgCode: string;
  status: TenantStatus;
  mode: TenantMode;
  isRoot: boolean;
  userCount: number;
  createdAt: string;
  deletedAt: string | null;
}

export interface TenantListResponse {
  tenants: TenantListItem[];
  total: number;
  filters: {
    q: string | null;
    status: TenantStatus | null;
  };
}

export interface TenantListQuery {
  q?: string;
  status?: TenantStatus;
}

// ─── List hook ───────────────────────────────────────────────────────────────

export function useTenants(query: TenantListQuery = {}) {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.status) params.set('status', query.status);
  const qs = params.toString();

  return useQuery<TenantListResponse, Error>({
    queryKey: ['platform-tenants', query],
    queryFn: () =>
      apiClient.get<TenantListResponse>(
        `/admin/platform/tenants${qs ? `?${qs}` : ''}`
      ),
  });
}

// ─── Detail hook (W1a-18 + R5 §4.1/§4.2/§4.6 extensions) ─────────────────────

export interface TenantDetail {
  id: string;
  name: string;
  slug: string;
  orgCode: string;
  status: TenantStatus;
  mode: TenantMode;
  isRoot: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  // R5 §4.2 — editable identity fields.
  region: string | null;
  timezone: string | null;
  primaryContactUserId: string | null;
  primaryContactEmail: string | null;
  primaryContactName: string | null;
}

export interface TenantStats {
  userCount: number;
  documentCount: number;
  conversationCount: number;
  messageCount: number;
  // R5 §4.1 — Overview KPIs.
  storageGb: number;
  tokensIn30d: number;
  tokensOut30d: number;
  gpuMinutes30d: number;
}

export type HealthStatus = 'healthy' | 'degraded' | 'down' | 'unknown';

export interface TenantHealthMicroStrip {
  api: HealthStatus;
  db: HealthStatus;
  qdrant: HealthStatus;
  valkey: HealthStatus;
  probedAt: string | null;
}

export interface TenantLifecycleEvent {
  id: string;
  action: string;
  sysadminEmail: string | null;
  details: unknown;
  createdAt: string;
}

export interface TenantAlert {
  id: string;
  destination: string;
  port: number | null;
  protocol: string | null;
  reason: string | null;
  decision: string;
  blockedAt: string;
  isTest: boolean;
}

export interface TenantAuditRow {
  id: string;
  userId: string | null;
  userEmail: string | null;
  action: string;
  category: string;
  outcome: string;
  createdAt: string;
}

/** R5 §4.6 — soft + hard cap pair. */
export interface SoftHardCap {
  soft: number;
  hard: number;
}

/** R5 §4.6 — per-tenant resource limits. */
export interface TenantResourceLimits {
  maxStorageGb?: SoftHardCap;
  maxMonthlyTokensIn?: SoftHardCap;
  maxMonthlyTokensOut?: SoftHardCap;
  maxGpuMinutesMonthly?: SoftHardCap;
  maxConcurrentUsers?: SoftHardCap;
  maxDocuments?: SoftHardCap;
  maxCustomAgents?: SoftHardCap;
  maxMcpServers?: SoftHardCap;
  burstAllowancePercent?: number;
  autoResetCadence?: 'monthly' | 'quarterly' | 'annual';
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export interface TenantDetailResponse {
  tenant: TenantDetail;
  stats: TenantStats;
  health: TenantHealthMicroStrip;
  recentLifecycle: TenantLifecycleEvent[];
  recentAlerts: TenantAlert[];
  recentAudit: TenantAuditRow[];
  /** W1a-23 — active compliance frameworks. */
  complianceFrameworks: string[];
  /** R5 §4.6 — resource limit overrides. */
  resourceLimits: TenantResourceLimits;
  /**
   * Foundations 1 / B0-T3 (SEC-6) — the tenant's two policy switches,
   * READ-ONLY on this surface. The sysadmin sets their initial values in the
   * provisioning wizard and never again: after creation they belong to the
   * tenant admin (Admin › Settings › General). `null` = they could not be read,
   * which means the tenant is currently denying both capabilities.
   *
   * OPTIONAL AS WELL AS NULLABLE (review BF-04). A web bundle can be newer than
   * the api bundle it talks to — `deploy-ai1.sh web` before `deploy-ai1.sh api`,
   * or a tab held open across a deploy with a cached detail response — and then
   * the field is ABSENT, not null. Callers must treat absent and null the same
   * (`!data.policySettings`), never `=== null`.
   */
  policySettings?: {
    allowWebSearch: boolean;
    allowExternalConnections: boolean;
  } | null;
}

export function useTenantDetail(tenantId: string) {
  return useQuery<TenantDetailResponse, Error>({
    queryKey: ['platform-tenant', tenantId],
    queryFn: () =>
      apiClient.get<TenantDetailResponse>(
        `/admin/platform/tenants/${tenantId}`
      ),
    enabled: !!tenantId,
  });
}

// ─── W1a-20 — lifecycle mutations ────────────────────────────────────────────

export interface TenantLifecycleResponse {
  tenant: {
    id: string;
    name: string;
    slug: string;
    isActive: boolean;
    suspendedAt: string | null;
    suspendedReason: string | null;
    deletedAt: string | null;
    purgeEligibleAt: string | null;
  };
  alreadyInState: boolean;
}

export function useSuspendTenant() {
  const qc = useQueryClient();
  return useMutation<TenantLifecycleResponse, Error, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) =>
      apiClient.post<TenantLifecycleResponse>(
        `/admin/platform/tenants/${id}/suspend`,
        { reason }
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['platform-tenant', vars.id] });
      qc.invalidateQueries({ queryKey: ['platform-tenants'] });
      qc.invalidateQueries({ queryKey: ['platform-overview'] });
    },
  });
}

export function useRestoreTenant() {
  const qc = useQueryClient();
  return useMutation<TenantLifecycleResponse, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post<TenantLifecycleResponse>(
        `/admin/platform/tenants/${id}/restore`,
        {}
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['platform-tenant', vars.id] });
      qc.invalidateQueries({ queryKey: ['platform-tenants'] });
      qc.invalidateQueries({ queryKey: ['platform-overview'] });
    },
  });
}

export function useDeleteTenant() {
  const qc = useQueryClient();
  return useMutation<TenantLifecycleResponse, Error, { id: string; confirmSlug: string }>({
    mutationFn: ({ id, confirmSlug }) =>
      apiClient.delete<TenantLifecycleResponse>(
        `/admin/platform/tenants/${id}`,
        { body: JSON.stringify({ confirmSlug }), headers: { 'Content-Type': 'application/json' } }
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['platform-tenant', vars.id] });
      qc.invalidateQueries({ queryKey: ['platform-tenants'] });
      qc.invalidateQueries({ queryKey: ['platform-overview'] });
    },
  });
}

export function useRestoreDeletedTenant() {
  const qc = useQueryClient();
  return useMutation<TenantLifecycleResponse, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post<TenantLifecycleResponse>(
        `/admin/platform/tenants/${id}/restore-deleted`,
        {}
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['platform-tenant', vars.id] });
      qc.invalidateQueries({ queryKey: ['platform-tenants'] });
      qc.invalidateQueries({ queryKey: ['platform-overview'] });
    },
  });
}

// ─── W1a-23 — compliance editor ──────────────────────────────────────────────
//
// Foundations 1 / B0-T1: the feature half of this endpoint is gone (there are
// no feature toggles). The hook keeps its name because the route it calls,
// PATCH /admin/platform/tenants/:id/features, keeps its path — and the
// compliance tab is its only caller.

export type ComplianceFramework = 'soc2' | 'iso27001' | 'itar' | 'fedramp' | 'hipaa';

export interface TenantFeaturesUpdateBody {
  complianceFrameworks?: ComplianceFramework[];
  /** Required (>= 8 chars) when the framework set actually changes. */
  reason?: string;
}

export interface TenantFeaturesResponse {
  tenant: {
    id: string;
    slug: string;
    complianceFrameworks: string[];
  };
  signingPolicyReconciled: boolean;
  diff: Record<string, { from: unknown; to: unknown }>;
}

export function useUpdateTenantFeatures() {
  const qc = useQueryClient();
  return useMutation<TenantFeaturesResponse, Error, { id: string; body: TenantFeaturesUpdateBody }>({
    mutationFn: ({ id, body }) =>
      apiClient.patch<TenantFeaturesResponse>(
        `/admin/platform/tenants/${id}/features`,
        body
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['platform-tenant', vars.id] });
      qc.invalidateQueries({ queryKey: ['platform-tenants'] });
      qc.invalidateQueries({ queryKey: ['platform-overview'] });
    },
  });
}
// ─── R5 §4.2 — Settings PATCH ────────────────────────────────────────────────

export interface TenantSettingsUpdateBody {
  name?: string;
  region?: string | null;
  timezone?: string | null;
  primaryContactUserId?: string | null;
  primaryContactEmail?: string | null;
}

export interface TenantSettingsResponse {
  tenant: {
    id: string;
    name: string;
    slug: string;
    region: string | null;
    timezone: string | null;
    primaryContactUserId: string | null;
    primaryContactEmail: string | null;
  };
  diff: Record<string, { from: unknown; to: unknown }>;
}

export function useUpdateTenantSettings() {
  const qc = useQueryClient();
  return useMutation<TenantSettingsResponse, Error, { id: string; body: TenantSettingsUpdateBody }>({
    mutationFn: ({ id, body }) =>
      apiClient.patch<TenantSettingsResponse>(
        `/admin/platform/tenants/${id}/settings`,
        body
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['platform-tenant', vars.id] });
      qc.invalidateQueries({ queryKey: ['platform-tenants'] });
    },
  });
}
// ─── R5 §4.6 — Resource Limits PATCH ─────────────────────────────────────────

export interface TenantLimitsUpdateBody extends TenantResourceLimits {
  mode?: 'merge' | 'replace';
}

export interface TenantLimitsResponse {
  tenant: {
    id: string;
    slug: string;
    resourceLimits: TenantResourceLimits;
  };
  diff: Record<string, { from: unknown; to: unknown }>;
}

export function useUpdateTenantLimits() {
  const qc = useQueryClient();
  return useMutation<TenantLimitsResponse, Error, { id: string; body: TenantLimitsUpdateBody }>({
    mutationFn: ({ id, body }) =>
      apiClient.patch<TenantLimitsResponse>(
        `/admin/platform/tenants/${id}/limits`,
        body
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['platform-tenant', vars.id] });
    },
  });
}

// ─── R5 §4.8 — Users roster GET (cursor-paginated) ───────────────────────────

export interface TenantUserRow {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  status: string | null;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
  joinedAt: string;
}

export interface TenantUsersResponse {
  users: TenantUserRow[];
  nextCursor: string | null;
  total: number;
}

export function useTenantUsers(tenantId: string, opts: { limit?: number; cursor?: string | null; q?: string } = {}) {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.cursor) params.set('cursor', opts.cursor);
  if (opts.q) params.set('q', opts.q);
  const qs = params.toString();
  return useQuery<TenantUsersResponse, Error>({
    queryKey: ['platform-tenant-users', tenantId, opts],
    queryFn: () =>
      apiClient.get<TenantUsersResponse>(
        `/admin/platform/tenants/${tenantId}/users${qs ? `?${qs}` : ''}`
      ),
    enabled: !!tenantId,
  });
}

// ─── R5 §4.9 — Tenant-scoped audit chain integrity ──────────────────────────

export interface TenantAuditIntegrityStatus {
  chainHeadSeq: number;
  chainHeadHash: string;
  chainHeadExists: boolean;
  isHealthy: boolean;
  brokenChainBanner: {
    active: boolean;
    brokenAt?: number;
    reason?: string;
  };
  lastWitnessAt: string | null;
  lastWitnessSeq: number | null;
  witnessKeyId: string | null;
}

export function useTenantAuditIntegrity(tenantId: string) {
  return useQuery<TenantAuditIntegrityStatus, Error>({
    queryKey: ['platform-tenant-audit-integrity', tenantId],
    queryFn: () =>
      apiClient.get<TenantAuditIntegrityStatus>(
        `/admin/platform/tenants/${tenantId}/audit/integrity`
      ),
    enabled: !!tenantId,
    refetchInterval: 30_000,
  });
}


// ─── R5 §4.4 — MCP catalog + tenant-MCP assignments ─────────────────────────

/** R5 §4.4 — MCP catalog row shape (subset of full sysadmin catalog row). */
export interface McpCatalogRow {
  id: string;
  slug: string;
  displayName: string;
  description: string;
  icon: string;
  hostingMode: 'platform_hosted' | 'remote_hosted' | 'rest_wrapped';
  protocol: string;
  trustLevel: 'official' | 'verified' | 'community' | 'experimental';
  marketplaceCategory: string;
  version: number;
  isPublished: boolean;
  retiredAt: string | null;
  activeInstallCount: number;
  // Catalog list paginates by (displayName, id); the row's cursor is
  // returned for follow-up paginated queries.
  cursor: string;
}

/**
 * R5 §4.4 — Tenant MCP assignment row.
 *
 * Matches `GET /admin/platform/mcp/tenants/:tenantId/assignments`
 * response shape (see packages/api/src/routes/v1/admin/mcp.ts:1130).
 */
export interface McpAssignmentRow {
  id: string;
  systemServerId: string;
  slug: string;
  displayName: string;
  hostingMode: 'platform_hosted' | 'remote_hosted' | 'rest_wrapped';
  protocol: string;
  trustLevel: 'official' | 'verified' | 'community' | 'experimental';
  installedVersion: number;
  isActive: boolean;
  healthStatus: string;
  installedAt: string;
  revokedAt: string | null;
  revocationReason: string | null;
}

export function useMcpCatalog(opts: {
  hostingMode?: 'platform_hosted' | 'remote_hosted' | 'rest_wrapped';
  trustLevel?: 'official' | 'verified' | 'community' | 'experimental';
  search?: string;
  publishedOnly?: boolean;
} = {}) {
  const params = new URLSearchParams();
  if (opts.hostingMode) params.set('hostingMode', opts.hostingMode);
  if (opts.trustLevel) params.set('trustLevel', opts.trustLevel);
  if (opts.search) params.set('search', opts.search);
  if (opts.publishedOnly) params.set('publishedOnly', 'true');
  // The catalog endpoint uses cursor pagination internally; for the
  // assignments UI we ask for a generous limit so we can render the
  // full list in the left pane without paging through.
  params.set('limit', '200');
  const qs = params.toString();
  return useQuery<{ items: McpCatalogRow[]; nextCursor: string | null }, Error>({
    queryKey: ['admin-mcp-catalog', opts],
    queryFn: () =>
      apiClient.get<{ items: McpCatalogRow[]; nextCursor: string | null }>(
        `/admin/platform/mcp/catalog${qs ? `?${qs}` : ''}`
      ),
  });
}

export function useTenantMcpAssignments(tenantId: string, includeRevoked = false) {
  const qs = includeRevoked ? '?includeRevoked=true' : '';
  return useQuery<{ items: McpAssignmentRow[] }, Error>({
    queryKey: ['platform-tenant-mcp-assignments', tenantId, includeRevoked],
    queryFn: () =>
      apiClient.get<{ items: McpAssignmentRow[] }>(
        `/admin/platform/mcp/tenants/${tenantId}/assignments${qs}`
      ),
    enabled: !!tenantId,
  });
}

export interface CreateMcpAssignmentBody {
  organizationId: string;
  systemServerId: string;
  endpointOverride?: string;
  /** The one strict install-config shape (SECC-T1); the api seals any credential. */
  tenantConfig?: McpTenantConfigInput;
}

/**
 * Create-assignment returns 201 `{id}` on insert, 200 `{id, alreadyExists: true}`
 * on idempotent re-issue. Both shapes are handled by the same caller.
 */
export interface CreateMcpAssignmentResponse {
  id: string;
  alreadyExists?: boolean;
}

export function useCreateMcpAssignment() {
  const qc = useQueryClient();
  return useMutation<CreateMcpAssignmentResponse, Error, CreateMcpAssignmentBody>({
    mutationFn: (body) =>
      apiClient.post<CreateMcpAssignmentResponse>(
        `/admin/platform/mcp/tenant-assignments`,
        body
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['platform-tenant-mcp-assignments', vars.organizationId] });
      qc.invalidateQueries({ queryKey: ['admin-mcp-catalog'] });
    },
  });
}

export function useRevokeMcpAssignment() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { id: string; tenantId: string; reason?: string }>({
    mutationFn: ({ id, reason }) =>
      apiClient.delete<{ ok: true }>(
        `/admin/platform/mcp/tenant-assignments/${id}`,
        reason
          ? { body: JSON.stringify({ reason }), headers: { 'Content-Type': 'application/json' } }
          : undefined,
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['platform-tenant-mcp-assignments', vars.tenantId] });
      qc.invalidateQueries({ queryKey: ['admin-mcp-catalog'] });
    },
  });
}
